import { UseQueryResult } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { getCurrentUid } from "@/api/common";
import { defineShareInfoInMiniProgram } from "@/utils/share";
import SharePanelWithContext, {
  SharePanelWithContextRef,
} from "@/component/share-panel/SharePanelWithContext";
import { PageDataConfig } from "@/types/config-page";
import { getBrowserEnv } from "@/utils/tools";
import { appendParamsToUrl } from "@/utils/url";

const { isMyApp, isWXApp } = getBrowserEnv();

type ShareInfo = {
  shareMainTitle: string;
  shareSubTitle: string;
  shareImgUrl: string;
  sharePageUrl: string;
};

type ConfigPageContextType = {
  pageCode?: string;
  versionCode?: string;
  navHeight?: number;
  configPageQuery?: UseQueryResult<PageDataConfig>;
  /**
   * 设置分享信息，
   * 实际不只是处理分享信息，还会把一些页面配置信息写入 localstorage，这是历史代码遗留的逻辑
   * TODO：以后找机会调整实现
   */
  handleShareInfo?: (data: PageDataConfig) => Promise<void>;
  handleShare?: (data?: PageDataConfig) => void;
  shareInfoRef?: React.Ref<ShareInfo>;
  onNavShareClick?: () => void;
  setNavHeight?: (height: number) => void;
  sharePath?: string;
  setSharePath?: (path: string) => void;
  pageOrigin?: string;
};

const ConfigPageContext = createContext<ConfigPageContextType>({});

export const ConfigPageProvider = ({
  children,
  configPageQuery,
  sharePath = "",
  pageOrigin = "",
  handleShareUrl = (params: string) => params,
}: {
  children: React.ReactNode;
  sharePath?: string;
  pageOrigin?: string;
  handleShareUrl?: (params: string) => string;
} & Omit<
  ConfigPageContextType,
  | "pageCode"
  | "handleShareInfo"
  | "handleShare"
  | "shareInfoRef"
  | "onNavShareClick"
  | "setNavHeight"
  | "navHeight"
  | "setSharePath"
>) => {
  const [navHeight, setNavHeight] = useState<number>(0);
  const [currentSharePath, setCurrentSharePath] = useState<string>(sharePath);
  const shareInfoRef = useRef<ShareInfo>({
    shareMainTitle: "",
    shareSubTitle: "",
    shareImgUrl: "",
    sharePageUrl: "",
  });

  const pageCode = configPageQuery?.data?.pageCode;
  const versionCode = configPageQuery?.data?.versionCode;

  const sharePanelRef = useRef<SharePanelWithContextRef>(null);

  const handleShareInfo: ConfigPageContextType["handleShareInfo"] = useCallback(
    async (data: PageDataConfig) => {
      const {
        shareTitle,
        pageTitle,
        shareUrl,
        sharePageUrl,
        moduleList = [],
      } = data;

      const userUid = await getCurrentUid();
      const shareCurrentParams = {
        pageCode: data?.pageCode || "",
        shareUid: userUid,
      };
      const shareCurrentUrl =
        currentSharePath ||
        `${window.location.origin}${window.location.pathname}#/configPage`;
      const defaultSharePageUrl = appendParamsToUrl(
        shareCurrentUrl,
        shareCurrentParams,
      );

      const currentSharePageUrl = handleShareUrl(sharePageUrl || defaultSharePageUrl)

      const shareInfoObj = {
        shareMainTitle: shareTitle || pageTitle,
        shareSubTitle: shareTitle || pageTitle,
        shareImgUrl: shareUrl || "",
        sharePageUrl: currentSharePageUrl
      };

      // 存储分享信息到localStorage
      localStorage.setItem(
        `shareInfoObj${data?.pageCode}`,
        JSON.stringify(shareInfoObj),
      );
      localStorage.setItem(
        `pageVersionDto${data?.pageCode}`,
        JSON.stringify(data),
      );

      if (moduleList.length > 0) {
        localStorage.setItem(
          `pageModuleDto${data?.pageCode}`,
          JSON.stringify(moduleList[0]),
        );

        moduleList.forEach((ele) => {
          if (ele.moduleType == "busi-ranking") {
            localStorage.setItem(
              `pageModuleDto${ele.moduleId}`,
              JSON.stringify(ele),
            );
          }
        });
      }

      // 只有在可分享时才设置小程序分享信息
      if (isMyApp || isWXApp) {
        defineShareInfoInMiniProgram({
          shareTitle: shareTitle || pageTitle,
          shareIcon: shareUrl || "",
          shareUrl: `/pages/webpage/index?pageUrl=${encodeURIComponent(currentSharePageUrl)}`,
        });
      }

      shareInfoRef.current = shareInfoObj;
    },
    [currentSharePath],
  );

  // 监听query数据变化，自动处理分享信息
  useEffect(() => {
    if (configPageQuery?.status === "success" && configPageQuery?.data) {
      handleShareInfo(configPageQuery.data);
    }
  }, [configPageQuery?.status, configPageQuery?.data, handleShareInfo]);

  const handleShare = (data?: PageDataConfig) => {
    if (configPageQuery?.data?.canShare === 0) {
      return;
    }
    if (data) {
      handleShareInfo(data);
      setTimeout(() => {
        sharePanelRef.current?.onOpen();
      }, 0);
    } else {
      sharePanelRef.current?.onClose();
    }
  };

  const onNavShareClick = () => {
    handleShare(configPageQuery?.data);
  };

  const handleSharePathChange = (path: string) => {
    setCurrentSharePath(path);
  };

  return (
    <ConfigPageContext.Provider
      value={{
        pageCode,
        versionCode,
        navHeight,
        configPageQuery,
        shareInfoRef,
        handleShareInfo,
        handleShare,
        onNavShareClick,
        setNavHeight,
        sharePath: currentSharePath,
        setSharePath: handleSharePathChange,
        pageOrigin,
      }}
    >
      {children}
      <SharePanelWithContext ref={sharePanelRef} />
    </ConfigPageContext.Provider>
  );
};

export const useConfigPage = () => {
  const context = useContext(ConfigPageContext);
  if (!context) {
    throw new Error("useConfigPage must be used within ConfigPageProvider");
  }
  return context;
};


export const PageOriginEnums = {
  VIP_GOODS: "/apps/charge-web/charge-vip.html#/vip-goods",
  VIP: "/apps/charge-web/charge-vip.html#/",
}